import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { Alert, Loading } from '../../components';
import { Badge, Button, Toggle } from '../../components/ui';
import { ProductSubmissionModal } from './ProductSubmissionModal';

import './catalog-approvals-panel.scss';

/**
 * Retail Circle Admin → Catalog Approvals (store-catalog feature).
 *
 * Master-only: the queue of store-submitted catalogue entries (brands /
 * models / products) and the platform auto-approve toggle. Approving a
 * product also approves its pending same-store brand/model chain
 * (AdminCatalogController::approve). Rejecting asks for a reason the owner
 * sees under Owner Admin → My Products. The toggle persists the moment it is
 * flipped (auto-save).
 */

const STATUSES = ['pending', 'approved', 'rejected'];

const TYPE_META = [
  ['products', 'Products'],
  ['models', 'Models'],
  ['brands', 'Brands'],
];

function rowLabel(type, row) {
  if (type === 'products') {
    return `${row.brand || '?'} ${row.model || '?'}${row.reference_number ? ` · ${row.reference_number}` : ''}`;
  }
  if (type === 'models') {
    return `${row.brand || '?'} ${row.name}`;
  }
  return row.name;
}

export function CatalogApprovalsPanel() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openProductId, setOpenProductId] = useState(null);

  const load = async (nextStatus = status) => {
    setError('');
    try {
      const res = await axios.get('/api/admin/catalog/submissions', { params: { status: nextStatus } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load catalogue submissions.');
    } finally {
      setLoading(false);
    }
  };

  // Mount-only fetch, same pattern as the sibling panels.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const switchStatus = (next) => {
    setStatus(next);
    setLoading(true);
    load(next);
  };

  const act = async (fn, fallbackMessage) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      const payload = e?.response?.data;
      setError(
        payload?.message
          || Object.values(payload?.errors || {}).flat().join(' ')
          || fallbackMessage
      );
    } finally {
      setBusy(false);
    }
  };

  const setAutoApprove = (next) => {
    act(async () => {
      const res = await axios.put('/api/admin/catalog/settings', { auto_approve: next });
      setData((d) => (d ? { ...d, auto_approve: res.data.auto_approve } : d));
    }, 'Could not update the auto-approve setting.');
  };

  const approve = (type, row) => {
    act(
      () => axios.post(`/api/admin/catalog/submissions/${type}/${row.id}/approve`),
      'Could not approve this submission.'
    );
  };

  const reject = (type, row) => {
    const reason = window.prompt(`Reject "${rowLabel(type, row)}"? The store sees this reason:`);
    if (reason === null) return; // cancelled
    act(
      () => axios.post(`/api/admin/catalog/submissions/${type}/${row.id}/reject`, { reason }),
      'Could not reject this submission.'
    );
  };

  if (loading && !data) return <Loading />;
  if (error && !data) return <Alert type="error" message={error} />;
  if (!data) return null;

  const total = TYPE_META.reduce((n, [key]) => n + (data[key]?.length || 0), 0);

  return (
    <div className="cap">
      <div className="cap__toggle-row">
        <Toggle
          checked={!!data.auto_approve}
          onChange={setAutoApprove}
          disabled={busy}
          label="Auto-approve store submissions"
        />
        <span className="cap__toggle-hint">
          {data.auto_approve
            ? 'Store catalogue entries go live for their store immediately, without review.'
            : 'Store catalogue entries wait in this queue until approved.'}
        </span>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="cap__filters">
        {STATUSES.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={s === status ? 'primary' : 'ghost'}
            onClick={() => switchStatus(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {total === 0 && (
        <p className="cap__empty">No {status} submissions.</p>
      )}

      {TYPE_META.map(([key, title]) => (
        (data[key]?.length || 0) > 0 && (
          <div className="cap__group" key={key}>
            <h4 className="cap__group-title">{title}</h4>
            <ul className="cap__list">
              {data[key].map((row) => (
                <li key={`${key}-${row.id}`} className="cap__row">
                  <span className="cap__cell cap__cell--main">{rowLabel(key, row)}</span>
                  {key === 'products' && (
                    <span className="cap__cell">
                      <Button size="sm" variant="secondary" onClick={() => setOpenProductId(row.id)}>
                        {status === 'pending' ? 'View / Edit' : 'View'}
                      </Button>
                    </span>
                  )}
                  <span className="cap__cell cap__cell--muted">
                    {row.store_name || `Store #${row.store_id_m}`}
                    {row.submitted_by_name?.trim() ? ` · ${row.submitted_by_name.trim()}` : ''}
                  </span>
                  {status === 'rejected' && row.rejection_reason && (
                    <span className="cap__cell cap__cell--reason" title={row.rejection_reason}>
                      {row.rejection_reason}
                    </span>
                  )}
                  {status === 'pending' && (
                    <span className="cap__cell cap__cell--actions">
                      <Button size="sm" variant="success" disabled={busy} onClick={() => approve(key, row)}>
                        Approve
                      </Button>
                      <Button size="sm" variant="danger" disabled={busy} onClick={() => reject(key, row)}>
                        Reject
                      </Button>
                    </span>
                  )}
                  {status === 'rejected' && (
                    <span className="cap__cell cap__cell--actions">
                      {/* The un-reject path — without it a product whose parent
                          brand/model was rejected could never be approved. */}
                      <Button size="sm" variant="success" disabled={busy} onClick={() => approve(key, row)}>
                        Approve
                      </Button>
                      <Badge tone="danger">Rejected</Badge>
                    </span>
                  )}
                  {status === 'approved' && (
                    <span className="cap__cell cap__cell--actions">
                      <Badge tone="success">Approved</Badge>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )
      ))}

      {openProductId !== null && (
        <ProductSubmissionModal
          productId={openProductId}
          onClose={() => setOpenProductId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

export default CatalogApprovalsPanel;
